import React, {useEffect, useState, useCallback} from "react";
import {createNewToken, fetchAllValidTokens, fetchTokensByUserId, revokeExistedToken, rotateExistedToken} from "../api";
import {
  Alert,
  Autocomplete,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Chip,
  Skeleton,
  TablePagination,
} from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import CloseIcon from "@mui/icons-material/Close";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import SearchIcon from "@mui/icons-material/Search";
import KeyIcon from "@mui/icons-material/Key";
import SaveIcon from "@mui/icons-material/Save";
import BlockIcon from "@mui/icons-material/Block";
import LockIcon from "@mui/icons-material/Lock";
import InfoIcon from "@mui/icons-material/Info";
import DescriptionIcon from "@mui/icons-material/Description";
import {Button} from "react-bootstrap";
import {formatAllValidTokens, getErrorMessage} from "../utils";
import {PORTAL_CHIP, PORTAL_GRADIENT, PORTAL_HEADER_ICON, PORTAL_PRIMARY, STATUS_CHIP_COLORS} from "./AdminStyles";

export default function AuthTokensCRUD({admin, userId, appAdmin, teamAdmin}) {
  
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isErrorData, setIsErrorData] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [isPendingData, setIsPendingData] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [bannerError, setBannerError] = useState("");
  const [toast, setToast] = useState({open: false, severity: "info", message: ""});
  
  const [generatedToken, setGeneratedToken] = useState("");
  const [openTokenDialog, setOpenTokenDialog] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [copied, setCopied] = useState(false);
  
  const [createDialog, setCreateDialog] = useState({open: false});
  const [formValues, setFormValues] = useState({name: "", description: "", expires_at: ""});
  
  const [revokeToken, setRevokeToken] = useState(null);
  const [revokeReason, setRevokeReason] = useState("");
  
  const [rotateToken, setRotateToken] = useState(null);
  const [rotateTokenFields, setRotateTokenFields] = useState({
    new_expires_in_days: "",
    grace_period_in_hours: "",
  });
  const [rotateErrors, setRotateErrors] = useState({
    new_expires_in_days: "",
    grace_period_in_hours: "",
  });
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Pagination handlers
  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Slice filteredData for current page
  const paginatedData = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );
  
  // Reset page to 0 when filteredData changes
  useEffect(() => {
    setPage(0);
  }, [filteredData]);
  
  const EXPIRY_OPTIONS = [
    {label: "1 Day", value: 1},
    {label: "1 Month (30 Days)", value: 30},
    {label: "3 Months (90 Days)", value: 90},
    {label: "6 Months (180 Days)", value: 180},
    {label: "1 Year (365 Days)", value: 365},
    {label: "No Expiration", value: -1}
  ];
  
  const showToast = (severity, message) => setToast({open: true, severity, message});
  const closeToast = () => setToast((t) => ({...t, open: false}));
  
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredData(data);
    } else {
      const lower = searchTerm.toLowerCase();
      setFilteredData(
        data.filter(
          (item) =>
            item.name?.toLowerCase().includes(lower) ||
            item.desc?.toLowerCase().includes(lower) ||
            item.created_by?.toLowerCase().includes(lower) ||
            item.expires_at?.toLowerCase().includes(lower) ||
            item.last_used_at?.toLowerCase().includes(lower) ||
            item.token_prefix?.toLowerCase().includes(lower) ||
            item.status?.toLowerCase().includes(lower)
        )
      );
    }
  }, [searchTerm, data]);
  
  function validateData(values) {
    const errors = {};
    if (!values?.name?.trim()) errors.name = "Token Name is required";
    if (values?.expires_at === "" || values?.expires_at === undefined) {
      errors.expires_at = "Token Expiry is required";
    }
    return errors;
  }
  
  const validateRotate = (fields, origToken) => {
    const errs = {new_expires_in_days: "", grace_period_in_hours: ""};
    const allowed = new Set(EXPIRY_OPTIONS.filter((o) => o.value !== null).map((o) => String(o.value)));
    const exp = String(fields.new_expires_in_days ?? "");
    if (!exp && fields.new_expires_in_days !== null) {
      errs.new_expires_in_days = "New expiry is required";
    } else if (fields.new_expires_in_days !== null && !allowed.has(exp)) {
      errs.new_expires_in_days = "Invalid expiry option";
    }
    const graceRaw = fields.grace_period_in_hours;
    if (graceRaw === "" || graceRaw === null || graceRaw === undefined) {
      errs.grace_period_in_hours = "Grace period is required";
    } else if (isNaN(Number(graceRaw)) || Number(graceRaw) < 0) {
      errs.grace_period_in_hours = "Grace period must be a positive number";
    }
    return errs;
  };
  
  const fetchRecordsData = useCallback(async () => {
    setIsErrorData(false);
    setBannerError("");
    try {
      const response = await (admin ? fetchAllValidTokens() : teamAdmin ? fetchTokensByUserId(userId) : fetchAllValidTokens());
      const formatted = formatAllValidTokens(response?.data ?? []);
      setData(formatted);
    } catch (error) {
      setIsErrorData(true);
      setData([]);
      const msg = getErrorMessage(error, "Error loading data");
      setBannerError(msg);
      showToast("error", msg);
    } finally {
      setIsLoadingData(false);
    }
  }, []);
  
  useEffect(() => {
    fetchRecordsData();
  }, [fetchRecordsData]);
  
  const openCreateDialog = () => {
    setFormValues({name: "", description: "", expires_at: ""});
    setValidationErrors({});
    setBannerError("");
    setCreateDialog({open: true});
  };
  
  const closeDialog = () => {
    setCreateDialog({open: false});
    setValidationErrors({});
  };
  
  const handleFormChange = (field, value) => {
    setFormValues((prev) => ({...prev, [field]: value}));
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({...prev, [field]: undefined}));
    }
  };
  
  const handleCreateRecord = async () => {
    const errors = validateData(formValues);
    if (Object.values(errors).some(Boolean)) {
      setValidationErrors(errors);
      showToast("error", "Please fix the highlighted fields.");
      return;
    }
    setIsPendingData(true);
    setBannerError("");
    try {
      const response = await createNewToken({
        name: formValues.name,
        desc: formValues.description,
        expires_in_days: formValues.expires_at,
      });
      setGeneratedToken(response?.data);
      setShowToken(false);
      setCopied(false);
      setOpenTokenDialog(true);
      await fetchRecordsData();
      closeDialog();
      showToast("success", "Token created successfully.");
    } catch (error) {
      const msg = getErrorMessage(error);
      setBannerError(msg);
      showToast("error", msg);
    } finally {
      setIsPendingData(false);
    }
  };
  
  const requestRevokeToken = (item) => {
    setBannerError("");
    setRevokeToken(item);
    setRevokeReason("");
  };
  
  const handleCancelRevoke = () => setRevokeToken(null);
  
  const handleRevokeToken = async () => {
    if (!revokeToken) return;
    if (!revokeReason.trim()) {
      showToast("error", "Revoke reason is required.");
      return;
    }
    try {
      await revokeExistedToken({token_id: revokeToken.id, revoke_reason: revokeReason});
      await fetchRecordsData();
      showToast("success", "Token revoked successfully.");
    } catch (error) {
      const msg = getErrorMessage(error);
      setBannerError(msg);
      showToast("error", msg);
    } finally {
      setRevokeToken(null);
      setRevokeReason("");
    }
  };
  
  const requestRotateToken = (item) => {
    setBannerError("");
    setRotateToken(item);
    setRotateTokenFields({new_expires_in_days: "", grace_period_in_hours: ""});
    setRotateErrors({new_expires_in_days: "", grace_period_in_hours: ""});
  };
  
  const handleCancelRotate = () => {
    setRotateToken(null);
    setRotateTokenFields({new_expires_in_days: "", grace_period_in_hours: ""});
    setRotateErrors({new_expires_in_days: "", grace_period_in_hours: ""});
  };
  
  const handleRotateToken = async () => {
    if (!rotateToken) return;
    const errs = validateRotate(rotateTokenFields, rotateToken);
    setRotateErrors(errs);
    if (Object.values(errs).some(Boolean)) {
      showToast("error", "Please fix the highlighted fields.");
      return;
    }
    const expSelected = rotateTokenFields.new_expires_in_days;
    const grace = Number(rotateTokenFields.grace_period_in_hours);
    const payload = {
      token_id: rotateToken.id,
      new_expires_in_days: expSelected,
      grace_period_in_hours: grace,
    };
    try {
      const response = await rotateExistedToken(payload);
      setGeneratedToken(response?.data);
      setShowToken(false);
      setCopied(false);
      setOpenTokenDialog(true);
      await fetchRecordsData();
      showToast("success", "Token rotated successfully.");
    } catch (error) {
      const msg = getErrorMessage(error);
      setBannerError(msg);
      showToast("error", msg);
    } finally {
      handleCancelRotate();
    }
  };
  
  const handleCopyToken = () => {
    navigator.clipboard.writeText(generatedToken);
    setCopied(true);
    showToast("success", "Token copied to clipboard!");
  };
  
  const TableSkeleton = () => (
    <TableBody>
      {[1, 2, 3, 4].map((i) => (
        <TableRow key={i}>
          <TableCell><Skeleton variant="text" width={120}/></TableCell>
          <TableCell><Skeleton variant="text" width={200}/></TableCell>
          <TableCell><Skeleton variant="text" width={100}/></TableCell>
          <TableCell><Skeleton variant="text" width={100}/></TableCell>
          <TableCell><Skeleton variant="text" width={100}/></TableCell>
          <TableCell><Skeleton variant="text" width={100}/></TableCell>
          <TableCell><Skeleton variant="text" width={100}/></TableCell>
          {admin && <TableCell><Skeleton variant="text" width={80}/></TableCell>}
        </TableRow>
      ))}
    </TableBody>
  );
  
  const EmptyState = () => (
    <TableRow>
      <TableCell colSpan={admin ? 8 : 7} sx={{py: 8, textAlign: "center"}}>
        <KeyIcon sx={{fontSize: 48, color: "#bdbdbd", mb: 1}}/>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No Auth Tokens Found
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
          {searchTerm ? "No results match your search criteria." : "Get started by creating your first auth token."}
        </Typography>
        {admin && !searchTerm && (
          <Button variant="primary" onClick={openCreateDialog}>
            <AddCircleIcon fontSize="small" style={{marginRight: 8}}/>
            Create Auth Token
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
  
  return (
    <Box sx={{maxWidth: 1400, mx: "auto", p: 1}}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 3,
          background: PORTAL_GRADIENT,
          color: "white",
        }}
      >
        <Stack direction={{xs: "column", sm: "row"}} justifyContent="space-between"
               alignItems={{xs: "stretch", sm: "center"}} spacing={2}>
          <Box>
            <Stack direction="row" alignItems="center" spacing={1}>
              <LockIcon sx={{fontSize: 28, color: PORTAL_HEADER_ICON}}/>
              <Typography variant="h6" fontWeight={700}>
                Token Management
              </Typography>
            </Stack>
            <Typography variant="body2" sx={{opacity: 0.9}}>
              Manage authentication tokens for artifacts download
            </Typography>
          </Box>
          {(admin || teamAdmin) && (
            <Button variant="light" onClick={openCreateDialog}
                    style={{display: "flex", alignItems: "center", gap: 8, fontWeight: 600}}>
              <AddCircleIcon fontSize="small"/>
              Create New
            </Button>
          )}
        </Stack>
      </Paper>
      
      {/* Error Banner */}
      {(bannerError || isErrorData) && (
        <Alert severity="error" sx={{mb: 2, borderRadius: 2}} onClose={() => setBannerError("")}>
          {bannerError || "Error loading data"}
        </Alert>
      )}
      
      {/* Search Bar */}
      <Paper elevation={0} sx={{p: 2, mb: 2, border: "1px solid #e0e0e0", borderRadius: 2}}>
        <Stack direction={{xs: "column", sm: "row"}} justifyContent="space-between"
               alignItems={{xs: "stretch", sm: "center"}} spacing={2}>
          <TextField
            placeholder="Search by token name, description, or creator..."
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{minWidth: 400}}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon
                color="action"/></InputAdornment>,
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm("")}><CloseIcon
                    fontSize="small"/></IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Chip
            label={`${filteredData.length} ${filteredData.length === 1 ? "active token" : "active tokens"}`}
            color="primary" variant="outlined"/>
        </Stack>
      </Paper>
      
      {/* Table */}
      <TableContainer component={Paper} elevation={0} sx={{border: "1px solid #e0e0e0", borderRadius: 2}}>
        <Table>
          <TableHead>
            <TableRow sx={{backgroundColor: "#f5f5f5"}}>
              <TableCell sx={{fontWeight: 700, width: 180}}>Token Name</TableCell>
              <TableCell sx={{fontWeight: 700}}>Description</TableCell>
              <TableCell sx={{fontWeight: 700}}>Token</TableCell>
              <TableCell sx={{fontWeight: 700}}>Status</TableCell>
              <TableCell sx={{fontWeight: 700, width: 150}}>Created By</TableCell>
              <TableCell sx={{fontWeight: 700, width: 150}}>Expires At</TableCell>
              <TableCell sx={{fontWeight: 700, width: 150}}>Last Used At</TableCell>
              {(admin || teamAdmin) &&
                <TableCell sx={{fontWeight: 700, width: 120, textAlign: "center"}}>Actions</TableCell>}
            </TableRow>
          </TableHead>
          {isLoadingData ? (
            <TableSkeleton/>
          ) : filteredData.length === 0 ? (
            <TableBody><EmptyState/></TableBody>
          ) : (
            <TableBody>
              {paginatedData.map((item) => (
                <TableRow
                  key={item.id}
                  sx={{
                    "&:hover": {backgroundColor: "#fafafa"},
                    "&:nth-of-type(even)": {backgroundColor: "#fcfcfc"},
                  }}
                >
                  <TableCell>
                    <Chip
                      icon={<KeyIcon sx={{fontSize: 16}}/>}
                      label={item.name}
                      size="small"
                      sx={PORTAL_CHIP}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      lineHeight: 1.6
                    }}>
                      {item.desc || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{item.token_prefix || "-"}</Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Chip
                        label={item.status || "-"}
                        size="small"
                        sx={{
                          ...(STATUS_CHIP_COLORS[item.status?.toLowerCase()] || {
                            backgroundColor: "#eceff4",
                            color: "#4d5b6b"
                          }),
                          fontWeight: 600,
                          fontSize: "0.75rem",
                          borderRadius: "8px",
                        }}
                      />
                      {item.status?.toLowerCase() === "rotated" && (
                        <Tooltip
                          title={
                            <Box sx={{fontSize: "0.875rem"}}>
                              <Typography variant="caption" display="block"
                                          sx={{color: "inherit"}}>
                                <strong>Rotated
                                  By:</strong> {item.rotated_by || "Unknown"}
                              </Typography>
                              <Typography variant="caption" display="block"
                                          sx={{color: "inherit"}}>
                                <strong>Rotated
                                  At:</strong> {item.rotated_at || "Unknown"}
                              </Typography>
                            </Box>
                          }
                        >
                          <InfoIcon
                            sx={{fontSize: 16, color: PORTAL_PRIMARY, cursor: "pointer"}}/>
                        </Tooltip>
                      )}
                      {item.status?.toLowerCase() === "revoked" && (
                        <Tooltip
                          title={
                            <Box sx={{fontSize: "0.875rem"}}>
                              <Typography variant="caption" display="block"
                                          sx={{color: "inherit"}}>
                                <strong>Revoked
                                  By:</strong> {item.revoked_by || "Unknown"}
                              </Typography>
                              <Typography variant="caption" display="block"
                                          sx={{color: "inherit"}}>
                                <strong>Revoked
                                  At:</strong> {item.revoked_at || "Unknown"}
                              </Typography>
                              <Typography variant="caption" display="block"
                                          sx={{color: "inherit"}}>
                                <strong>Reason:</strong> {item.revoke_reason || "Unknown"}
                              </Typography>
                            </Box>
                          }
                        >
                          <InfoIcon
                            sx={{fontSize: 16, color: PORTAL_PRIMARY, cursor: "pointer"}}/>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{item.created_by || "-"}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{item.expires_at || "Never"}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{item.last_used_at || "Never"}</Typography>
                  </TableCell>
                  {(admin || teamAdmin) && (
                    <TableCell sx={{textAlign: "center"}}>
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip
                          title={item.status?.toLowerCase() !== "active" ? "" : "Rotate"}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => requestRotateToken(item)}
                              disabled={item.status?.toLowerCase() !== "active"}
                              sx={{color: PORTAL_PRIMARY}}
                            >
                              <AutorenewIcon fontSize="small"/>
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={"Revoke"}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => requestRevokeToken(item)}
                              disabled={!['active', 'rotated'].includes(item.status?.toLowerCase())}
                              sx={{color: "#d32f2f"}}
                            >
                              <BlockIcon fontSize="small"/>
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          )}
        </Table>
        {/* Table Pagination */}
        <Box sx={{display: "flex", justifyContent: "flex-end", mt: 1}}>
          <TablePagination
            component="div"
            count={filteredData.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            showFirstButton
            showLastButton
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              px: 2,
              "& .MuiTablePagination-toolbar": {
                display: "flex",
                alignItems: "center",
                width: "100%",
                flexWrap: "wrap",
                gap: 1,
                minHeight: 48,
              },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                margin: 0,
                alignSelf: "center",
              },
              "& .MuiTablePagination-actions": {
                marginLeft: 1,
              },
            }}
          />
        </Box>
      </TableContainer>
      
      {/* Create Dialog */}
      <Dialog open={createDialog.open} onClose={closeDialog} maxWidth="sm" fullWidth
              PaperProps={{sx: {borderRadius: 3}}}>
        <DialogTitle sx={{background: PORTAL_GRADIENT, color: "white", py: 2}}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <AddCircleIcon/>
            <Typography variant="h6" fontWeight={600}>Create New Token</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{pt: 3, pb: 2}}>
          <Stack spacing={3} sx={{mt: 3}}>
            <TextField
              label="Token Name"
              value={formValues.name}
              onChange={(e) => handleFormChange("name", e.target.value)}
              error={!!validationErrors.name}
              helperText={validationErrors.name}
              fullWidth
              required
              placeholder="Enter token name..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <KeyIcon color="action"/>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Description"
              value={formValues.description}
              onChange={(e) => handleFormChange("description", e.target.value)}
              fullWidth
              placeholder="Enter token description (optional)..."
              multiline
              minRows={2}
              maxRows={4}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <DescriptionIcon color="action"/>
                  </InputAdornment>
                ),
              }}
            />
            <Autocomplete
              options={EXPIRY_OPTIONS}
              getOptionLabel={(option) => option.label}
              value={EXPIRY_OPTIONS.find((o) => o.value === formValues.expires_at) || null}
              onChange={(e, newValue) => handleFormChange("expires_at", newValue?.value ?? "")}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Token Expiry"
                  error={!!validationErrors.expires_at}
                  helperText={validationErrors.expires_at}
                  required
                  placeholder="Select expiry duration..."
                />
              )}
            />
          </Stack>
        </DialogContent>
        <Divider/>
        <DialogActions sx={{p: 2, gap: 1}}>
          <Button variant="outline-secondary" onClick={closeDialog}>Cancel</Button>
          <Button variant="primary" onClick={handleCreateRecord} disabled={isPendingData}
                  style={{display: "flex", alignItems: "center", gap: 6}}>
            <SaveIcon fontSize="small"/>
            {isPendingData ? "Creating..." : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Revoke Dialog */}
      <Dialog open={!!revokeToken} onClose={handleCancelRevoke} maxWidth="sm" fullWidth
              PaperProps={{sx: {borderRadius: 3}}}>
        <DialogTitle
          sx={{background: "linear-gradient(135deg, #d32f2f 0%, #c62828 100%)", color: "white", py: 2}}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <BlockIcon/>
            <Typography variant="h6" fontWeight={600}>Revoke Token - {revokeToken?.name}</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{pt: 3, pb: 2}}>
          <Alert severity="warning" sx={{mb: 3, borderRadius: 2}}>
            This action cannot be undone. The token will be permanently revoked.
          </Alert>
          <TextField
            label="Revoke Reason"
            value={revokeReason}
            onChange={(e) => setRevokeReason(e.target.value.slice(0, 1024))}
            fullWidth
            required
            placeholder="Enter reason for revoking this token..."
            multiline
            minRows={2}
            maxRows={4}
            inputProps={{maxLength: 1024}}
            helperText={`${revokeReason.length}/1024 characters`}
          />
        </DialogContent>
        <Divider/>
        <DialogActions sx={{p: 2, gap: 1}}>
          <Button variant="outline-secondary" onClick={handleCancelRevoke}>Cancel</Button>
          <Button variant="danger" onClick={handleRevokeToken}
                  style={{display: "flex", alignItems: "center", gap: 6}}>
            <BlockIcon fontSize="small"/>
            Revoke Token
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Rotate Dialog */}
      <Dialog open={!!rotateToken} onClose={handleCancelRotate} maxWidth="sm" fullWidth
              PaperProps={{sx: {borderRadius: 3}}}>
        <DialogTitle sx={{background: PORTAL_GRADIENT, color: "white", py: 2}}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <AutorenewIcon/>
            <Typography variant="h6" fontWeight={600}>Rotate Token - {rotateToken?.name}</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{pt: 3, pb: 2}}>
          <Alert severity="info" sx={{mb: 3, borderRadius: 2}}>
            A new token will be generated. The old token will remain valid during the grace period.
          </Alert>
          <Stack spacing={3}>
            <Autocomplete
              options={EXPIRY_OPTIONS.filter((o) => o.value !== null)}
              getOptionLabel={(option) => option.label}
              value={EXPIRY_OPTIONS.find((o) => o.value === rotateTokenFields.new_expires_in_days) || null}
              onChange={(e, newValue) => setRotateTokenFields((prev) => ({
                ...prev,
                new_expires_in_days: newValue?.value || ""
              }))}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="New Expiry Duration"
                  error={!!rotateErrors.new_expires_in_days}
                  helperText={rotateErrors.new_expires_in_days}
                  required
                  placeholder="Select new expiry duration..."
                />
              )}
            />
            <TextField
              label="Grace Period (Hours)"
              type="number"
              value={rotateTokenFields.grace_period_in_hours}
              onChange={(e) => setRotateTokenFields((prev) => ({
                ...prev,
                grace_period_in_hours: e.target.value
              }))}
              error={!!rotateErrors.grace_period_in_hours}
              helperText={rotateErrors.grace_period_in_hours || "How long the old token remains valid"}
              fullWidth
              required
              placeholder="Enter grace period in hours..."
              inputProps={{min: 0}}
            />
          </Stack>
        </DialogContent>
        <Divider/>
        <DialogActions sx={{p: 2, gap: 1}}>
          <Button variant="outline-secondary" onClick={handleCancelRotate}>Cancel</Button>
          <Button variant="primary" onClick={handleRotateToken}
                  style={{display: "flex", alignItems: "center", gap: 6}}>
            <AutorenewIcon fontSize="small"/>
            Rotate Token
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Token Display Dialog */}
      <Dialog open={openTokenDialog} onClose={() => setOpenTokenDialog(false)} maxWidth="sm" fullWidth
              PaperProps={{sx: {borderRadius: 3}}}>
        <DialogTitle
          sx={{background: "linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)", color: "white", py: 2}}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CheckBoxIcon/>
            <Typography variant="h6" fontWeight={600}>Token Generated Successfully</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{pt: 3, pb: 2}}>
          <Alert severity="warning" sx={{mb: 3, borderRadius: 2}}>
            Copy your token now. You won't be able to see it again!
          </Alert>
          <TextField
            label="Your Token"
            value={showToken ? generatedToken : "•".repeat(40)}
            fullWidth
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowToken(!showToken)} size="small">
                    {showToken ? <VisibilityOffIcon/> : <VisibilityIcon/>}
                  </IconButton>
                  <IconButton onClick={handleCopyToken} size="small"
                              color={copied ? "success" : "default"}>
                    {copied ? <CheckBoxIcon/> : <ContentCopyIcon/>}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <Divider/>
        <DialogActions sx={{p: 2}}>
          <Button variant="primary" onClick={() => setOpenTokenDialog(false)}>Done</Button>
        </DialogActions>
      </Dialog>
      
      {/* Toast */}
      <Snackbar open={toast.open} autoHideDuration={4000} onClose={closeToast}
                anchorOrigin={{vertical: "top", horizontal: "center"}}>
        <Alert onClose={closeToast} severity={toast.severity} variant="filled"
               sx={{width: "100%", borderRadius: 2}}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
